import { supabase } from './supabaseClient';
import { 
  sendEmail, 
  getRegistrationEmailTemplate, 
  getBookingConfirmationTemplate 
} from './emailService';
import { z } from 'zod';
import { parsePhoneNumber } from 'libphonenumber-js';
import bcrypt from 'bcryptjs';

// Types per il profilo utente esteso
export interface UserProfile {
  id: string;
  phone_number?: string;
  terms_accepted: boolean;
  privacy_accepted: boolean;
  newsletter_subscribed: boolean;
  full_name?: string;
  date_of_birth?: string;
  nationality?: string;
  document_type?: 'carta_identita' | 'passaporto' | 'patente';
  document_front_url?: string;
  document_back_url?: string;
  selfie_url?: string;
  address_street?: string;
  address_city?: string;
  address_postal_code?: string;
  address_country?: string;
  two_factor_enabled: boolean;
  security_question?: string;
  security_answer_hash?: string;
  bio?: string;
  profile_image_url?: string;
  preferences: any;
  facebook_verified: boolean;
  google_verified: boolean;
  linkedin_verified: boolean;
  identity_verified: boolean;
  email_verified: boolean;
  phone_verified: boolean;
  registration_phase: 1 | 2 | 3;
  verification_status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

// Types per prenotazioni
export interface Booking {
  id: string;
  user_id: string;
  accommodation_id: number;
  check_in: string;
  check_out: string;
  guests_count: number;
  base_price: number;
  cleaning_fee: number;
  service_fee: number;
  taxes: number;
  total_price: number;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  payment_status: 'pending' | 'paid' | 'refunded';
  guest_name: string;
  guest_email: string;
  guest_phone?: string;
  special_requests?: string;
  host_notes?: string;
  airbnb_booking_id?: string;
  avaibook_booking_id?: string;
  external_source: 'direct' | 'airbnb' | 'avaibook';
  created_at: string;
  updated_at: string;
}

// Schema di validazione per la registrazione Fase 1
export const registrationPhase1Schema = z.object({
  email: z.string().email('Email non valida'),
  phone: z.string().min(10, 'Numero di telefono non valido'),
  password: z.string().min(8, 'Password deve essere almeno 8 caratteri'),
  confirmPassword: z.string(),
  termsAccepted: z.boolean().refine(val => val === true, 'Devi accettare i termini'),
  privacyAccepted: z.boolean().refine(val => val === true, 'Devi accettare la privacy policy'),
  newsletterSubscribed: z.boolean().optional()
}).refine(data => data.password === data.confirmPassword, {
  message: "Le password non corrispondono",
  path: ["confirmPassword"]
});

// Schema di validazione per la registrazione Fase 2
export const registrationPhase2Schema = z.object({
  fullName: z.string().min(2, 'Nome completo richiesto'),
  dateOfBirth: z.string().refine(date => {
    const birthDate = new Date(date);
    const today = new Date();
    const age = today.getFullYear() - birthDate.getFullYear();
    return age >= 18;
  }, 'Devi avere almeno 18 anni'),
  nationality: z.string().min(2, 'Nazionalità richiesta'),
  documentType: z.enum(['carta_identita', 'passaporto', 'patente']),
  addressStreet: z.string().optional(),
  addressCity: z.string().optional(),
  addressPostalCode: z.string().optional(),
  addressCountry: z.string().optional()
});

// FUNZIONI DI REGISTRAZIONE E AUTENTICAZIONE

// Registrazione Fase 1 - Creazione account base
export const registerPhase1 = async (data: z.infer<typeof registrationPhase1Schema>) => {
  try {
    // Valida i dati
    const validatedData = registrationPhase1Schema.parse(data);
    
    // Valida il numero di telefono
    const phoneNumber = parsePhoneNumber(validatedData.phone, 'IT');
    if (!phoneNumber?.isValid()) {
      throw new Error('Numero di telefono non valido');
    }

    // Registrazione con Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: validatedData.email,
      password: validatedData.password,
      options: {
        data: {
          full_name: '', // Verrà aggiornato nella fase 2
        }
      }
    });

    if (authError) throw authError;
    if (!authData.user) throw new Error('Errore nella creazione utente');

    // Crea profilo utente
    const { error: profileError } = await supabase
      .from('user_profiles')
      .insert({
        id: authData.user.id,
        phone_number: phoneNumber.formatInternational(),
        terms_accepted: validatedData.termsAccepted,
        privacy_accepted: validatedData.privacyAccepted,
        newsletter_subscribed: validatedData.newsletterSubscribed || true,
        registration_phase: 1,
        email_verified: false,
        phone_verified: false
      });

    if (profileError) throw profileError;

    // Invia email di verifica
    if (authData.user.email) {
      const verificationLink = `${window.location.origin}/verify-email?token=${authData.user.email_confirmed_at}`;
      const emailTemplate = getRegistrationEmailTemplate(
        validatedData.email.split('@')[0], // Nome temporaneo
        verificationLink
      );
      
      await sendEmail(authData.user.email, emailTemplate);
    }

    return { 
      success: true, 
      user: authData.user,
      message: 'Registrazione completata! Controlla la tua email per confermare l\'account.' 
    };
  } catch (error) {
    console.error('Errore registrazione fase 1:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore sconosciuto' 
    };
  }
};

// Registrazione Fase 2 - Completamento profilo
export const registerPhase2 = async (data: z.infer<typeof registrationPhase2Schema>) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utente non autenticato');

    // Valida i dati
    const validatedData = registrationPhase2Schema.parse(data);

    // Aggiorna profilo utente
    const { error: updateError } = await supabase
      .from('user_profiles')
      .update({
        full_name: validatedData.fullName,
        date_of_birth: validatedData.dateOfBirth,
        nationality: validatedData.nationality,
        document_type: validatedData.documentType,
        address_street: validatedData.addressStreet,
        address_city: validatedData.addressCity,
        address_postal_code: validatedData.addressPostalCode,
        address_country: validatedData.addressCountry,
        registration_phase: 2
      })
      .eq('id', user.id);

    if (updateError) throw updateError;

    return { success: true, message: 'Profilo aggiornato con successo!' };
  } catch (error) {
    console.error('Errore registrazione fase 2:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore sconosciuto' 
    };
  }
};

// Upload documenti identità
export const uploadIdentityDocument = async (
  file: File, 
  documentType: 'document_front' | 'document_back' | 'selfie'
): Promise<{ success: boolean; url?: string; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utente non autenticato');

    // Upload file
    const fileExt = file.name.split('.').pop();
    const fileName = `${user.id}/${documentType}_${Date.now()}.${fileExt}`;
    
    const { data: uploadData, error: uploadError } = await supabase
      .storage
      .from('documents')
      .upload(fileName, file);

    if (uploadError) throw uploadError;

    // Ottieni URL pubblico
    const { data: { publicUrl } } = supabase
      .storage
      .from('documents')
      .getPublicUrl(uploadData.path);

    // Salva riferimento nel database
    const { error: dbError } = await supabase
      .from('uploaded_documents')
      .insert({
        user_id: user.id,
        document_type: documentType,
        file_url: publicUrl,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type
      });

    if (dbError) throw dbError;

    // Aggiorna profilo utente
    const updateField = `${documentType}_url`;
    const { error: profileError } = await supabase
      .from('user_profiles')
      .update({ [updateField]: publicUrl })
      .eq('id', user.id);

    if (profileError) throw profileError;

    return { success: true, url: publicUrl };
  } catch (error) {
    console.error('Errore upload documento:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore upload' 
    };
  }
};

// FUNZIONI PER PROFILO UTENTE

// Ottieni profilo utente completo
export const getUserProfile = async (): Promise<UserProfile | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Errore recupero profilo:', error);
    return null;
  }
};

// Aggiorna profilo utente
export const updateUserProfile = async (updates: Partial<UserProfile>) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utente non autenticato');

    const { error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', user.id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Errore aggiornamento profilo:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore aggiornamento' 
    };
  }
};

// FUNZIONI PER PRENOTAZIONI

// Verifica disponibilità
export const checkAvailability = async (
  accommodationId: number, 
  checkIn: string, 
  checkOut: string
): Promise<{ available: boolean; price?: number }> => {
  try {
    const { data, error } = await supabase
      .from('availability')
      .select('*')
      .eq('accommodation_id', accommodationId)
      .gte('date', checkIn)
      .lt('date', checkOut)
      .eq('available', true);

    if (error) throw error;

    // Verifica che tutti i giorni siano disponibili
    const startDate = new Date(checkIn);
    const endDate = new Date(checkOut);
    const nights = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    const available = data && data.length >= nights;
    const totalPrice = data?.reduce((sum, day) => sum + (day.price || 0), 0);

    return { available, price: totalPrice };
  } catch (error) {
    console.error('Errore verifica disponibilità:', error);
    return { available: false };
  }
};

// Crea prenotazione
export const createBooking = async (bookingData: {
  accommodationId: number;
  checkIn: string;
  checkOut: string;
  guestsCount: number;
  guestName: string;
  guestEmail: string;
  guestPhone?: string;
  specialRequests?: string;
}): Promise<{ success: boolean; booking?: Booking; error?: string }> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utente non autenticato');

    // Verifica disponibilità
    const availability = await checkAvailability(
      bookingData.accommodationId, 
      bookingData.checkIn, 
      bookingData.checkOut
    );

    if (!availability.available) {
      throw new Error('Date non disponibili');
    }

    // Calcola prezzi
    const basePrice = availability.price || 0;
    const cleaningFee = basePrice * 0.1; // 10% del prezzo base
    const serviceFee = basePrice * 0.05; // 5% del prezzo base
    const taxes = basePrice * 0.1; // 10% di tasse
    const totalPrice = basePrice + cleaningFee + serviceFee + taxes;

    // Crea prenotazione
    const { data: booking, error } = await supabase
      .from('bookings')
      .insert({
        user_id: user.id,
        accommodation_id: bookingData.accommodationId,
        check_in: bookingData.checkIn,
        check_out: bookingData.checkOut,
        guests_count: bookingData.guestsCount,
        base_price: basePrice,
        cleaning_fee: cleaningFee,
        service_fee: serviceFee,
        taxes: taxes,
        total_price: totalPrice,
        guest_name: bookingData.guestName,
        guest_email: bookingData.guestEmail,
        guest_phone: bookingData.guestPhone,
        special_requests: bookingData.specialRequests,
        external_source: 'direct',
        status: 'pending',
        payment_status: 'pending'
      })
      .select()
      .single();

    if (error) throw error;

    return { success: true, booking };
  } catch (error) {
    console.error('Errore creazione prenotazione:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore creazione prenotazione' 
    };
  }
};

// Ottieni prenotazioni utente
export const getUserBookings = async (): Promise<Booking[]> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    const { data, error } = await supabase
      .from('bookings')
      .select(`
        *,
        accommodations:accommodation_id (
          title_it,
          title_en,
          image,
          location_it,
          location_en
        )
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Errore recupero prenotazioni:', error);
    return [];
  }
};

// Conferma prenotazione (dopo pagamento)
export const confirmBooking = async (bookingId: string) => {
  try {
    const { data: booking, error: fetchError } = await supabase
      .from('bookings')
      .select('*')
      .eq('id', bookingId)
      .single();

    if (fetchError) throw fetchError;

    // Aggiorna status prenotazione
    const { error: updateError } = await supabase
      .from('bookings')
      .update({
        status: 'confirmed',
        payment_status: 'paid'
      })
      .eq('id', bookingId);

    if (updateError) throw updateError;

    // Invia email di conferma
    const emailTemplate = getBookingConfirmationTemplate({
      guestName: booking.guest_name,
      accommodationTitle: 'Alloggio Confermato', // TODO: fetch dal database
      checkIn: new Date(booking.check_in).toLocaleDateString('it-IT'),
      checkOut: new Date(booking.check_out).toLocaleDateString('it-IT'),
      totalPrice: `€${booking.total_price.toFixed(2)}`,
      bookingId: booking.id
    });

    await sendEmail(booking.guest_email, emailTemplate);

    return { success: true };
  } catch (error) {
    console.error('Errore conferma prenotazione:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore conferma prenotazione' 
    };
  }
};

// FUNZIONI DI VERIFICA E SICUREZZA

// Verifica email
export const verifyEmail = async (token: string) => {
  try {
    // TODO: Implementare logica di verifica email con token
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utente non trovato');

    const { error } = await supabase
      .from('user_profiles')
      .update({ email_verified: true })
      .eq('id', user.id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Errore verifica email:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore verifica email' 
    };
  }
};

// Genera e invia codice SMS per verifica telefono
export const sendPhoneVerificationCode = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utente non autenticato');

    const profile = await getUserProfile();
    if (!profile?.phone_number) throw new Error('Numero di telefono non trovato');

    // Genera codice 6 cifre
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    
    // Salva codice nel database (con scadenza)
    const { error } = await supabase
      .from('verification_codes')
      .insert({
        user_id: user.id,
        code: await bcrypt.hash(code, 10),
        type: 'phone',
        expires_at: new Date(Date.now() + 10 * 60 * 1000).toISOString() // 10 minuti
      });

    if (error) throw error;

    // TODO: Inviare SMS con servizio terzo (Twilio, etc.)
    console.log('Codice verifica SMS:', code); // Per testing

    return { success: true, message: 'Codice inviato via SMS' };
  } catch (error) {
    console.error('Errore invio codice SMS:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore invio codice' 
    };
  }
};

// Verifica codice SMS
export const verifyPhoneCode = async (code: string) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Utente non autenticato');

    // Verifica codice dal database
    const { data: verificationCodes, error: fetchError } = await supabase
      .from('verification_codes')
      .select('*')
      .eq('user_id', user.id)
      .eq('type', 'phone')
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false })
      .limit(1);

    if (fetchError) throw fetchError;
    if (!verificationCodes || verificationCodes.length === 0) {
      throw new Error('Codice scaduto o non trovato');
    }

    const isValid = await bcrypt.compare(code, verificationCodes[0].code);
    if (!isValid) throw new Error('Codice non valido');

    // Aggiorna profilo utente
    const { error: updateError } = await supabase
      .from('user_profiles')
      .update({ phone_verified: true })
      .eq('id', user.id);

    if (updateError) throw updateError;

    // Elimina codice usato
    await supabase
      .from('verification_codes')
      .delete()
      .eq('id', verificationCodes[0].id);

    return { success: true, message: 'Telefono verificato con successo!' };
  } catch (error) {
    console.error('Errore verifica codice:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Errore verifica codice' 
    };
  }
}; 